import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Menu, Transition, Disclosure, Switch } from "@headlessui/react";
import { useSelector } from "react-redux";
import { useAuth } from "../../context/auth-context";
import {
  CalendarIcon,
  MenuAlt2Icon,
  ChartPieIcon,
  AcademicCapIcon,
  HomeIcon,
  XIcon,
  UserIcon,
  NewspaperIcon,
  BookOpenIcon,
  HeartIcon,
  ChatAltIcon,
  BriefcaseIcon,
  ChartBarIcon,
  ChevronDownIcon,
  CogIcon,
  FolderIcon as FolderIconOutline,
  LibraryIcon,
  CreditCardIcon,
  BellIcon,
  UsersIcon,
  SearchIcon,
  EmojiHappyIcon,
  LoginIcon,
  PresentationChartBarIcon,
  DatabaseIcon,
  TemplateIcon,
  ClockIcon,
  AnnotationIcon,
  DocumentTextIcon,
  MailIcon,
  LockOpenIcon,
  LockClosedIcon,
  ClipboardListIcon,
  LightningBoltIcon,
  SearchCircleIcon,
  DotsVerticalIcon,
  SwitchHorizontalIcon,
  ScaleIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/outline";
import {
  BellIcon as BellIconSolid,
  UserIcon as UserIconSolid,
  UsersIcon as UsersIconSolid,
  CogIcon as CogIconSolid,
  EmojiHappyIcon as EmojiHappyIconSolid,
  LoginIcon as LoginIconSolid,
  PresentationChartBarIcon as PresentationChartBarIconSolid,
  DatabaseIcon as DatabaseIconSolid,
  BookOpenIcon as BookOpenIconSolid,
  TemplateIcon as TemplateIconSolid,
  AnnotationIcon as AnnotationIconSolid,
  ClockIcon as ClockIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  MailIcon as MailIconSolid,
  LockClosedIcon as LockClosedIconSolid
} from "@heroicons/react/solid";
import useNav from "../../hooks/useNav";
import { authenticateAdvisorAdmin } from "../../helpers/jwtAuth";
import { TbBread, TbLayoutDashboard } from "react-icons/tb";
import { useQuery } from "react-query";
import ApiRequest from "../../api/ApiRequest";
import { API_AUTH_TYPES, API_ROUTES } from "../../api/apiConstants";
import Notifications from "../Notifications";
import { classNames } from "../misc/styling";
import { FolderIcon } from "@heroicons/react/solid";
import RouteLoadingSpinner from "../loading/RouteLoadingSpinner";
import { CommandPaletteHouseholdSearch } from "../comboboxes/index";
import { ToolTipHoverContainer, ToolTipLeft } from "../ToolTips";
import useConfig from "../../hooks/useConfig";
import ParabotInterface from "../ai/Parabot-Interface";
import ReminderButton from "../Reminders/ReminderButton";
import EmailMessages from "../../routes/AdvisorPortal/emails/components/EmailMessages";
import Toasts from "../Toasts";
import HHSearch from "../../routes/Client-Portal/HHSearch";

// images
const abundoLogo = "https://res.cloudinary.com/abundo-wealth-assets/image/upload/v1658176803/Abundo%20Wealth%20Assets/components/logos/Abundo_logo_mtnbxi_zg4tju.webp";


// Function to get current page title and description
const getCurrentPageInfo = (isAdvisor) => {


  // Page titles and descriptions configuration
  const PAGE_TITLES = {
    '/financial-story': {
      title: 'Financial Story',
      description: 'Your personalized financial journey and actionable tasks.'
    },
    '/notes': {
      title: isAdvisor ? 'Household Notes' : 'My Notes',
      description: isAdvisor ? 'Save notes about your client\'s financial situation, or goals.' : 'Your personal space to save notes about anything related to your financial situation, or goals. Your advisor can see these.'
    },
    '/vault': {
      title: 'Vault',
      description: 'Securely manage and share documents with your Abundo advisor'
    },

    '/advisor': {
      title: 'Advisor Household Dashboard',
      description: ''
    },
    '/partners': {
      title: 'Partners',
      description: 'Explore trusted resources and partners that align with Abundo\'s commitment to client-centered financial well-being'
    }
  };
  const currentPath = window.location.pathname;

  // Handle client routes
  for (const [path, info] of Object.entries(PAGE_TITLES)) {
    if (currentPath.includes(path)) {
      return info;
    }
  }

  return null;
};

// Page Header Component
const PageHeader = ({ isAdvisor }) => {
  const pageInfo = getCurrentPageInfo(isAdvisor);
  if (!pageInfo) return null;

  return (
    <div className="mb-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
        {pageInfo.title}
      </h1>
      <p className="text-sm sm:text-base text-gray-600 mt-1">
        {pageInfo.description}
      </p>
    </div>
  );
};

const RightCapitalIcon = ({ className }) => {
  return (
    <img src="https://res.cloudinary.com/abundo-wealth-assets/image/upload/v1746466254/Abundo%20Wealth%20Assets/logos/RC_logo_cwnvmo.svg" alt="right capital" className={className} />
  )
}
export default function SideNavBar({ children, bgColor, useContainer = true, topNavbarType = 'standard' }) {
  const nav = useNav();
  const { logout } = useAuth();
  const config = useConfig();
  const { householdId: configHouseholdId, isAdvisor } = config || {};


  // Fallback values to prevent undefined errors
  const safeIsAdvisor = isAdvisor ?? false;
  const safeConfigHouseholdId = configHouseholdId || 'me';

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filteredNavigation, setFilteredNavigation] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isManager, setIsManager] = useState(false);
  const [userId, setUserId] = useState(null);
  const [meetingMode, setMeetingMode] = useState(false);
  const [budgetDropdownHover, setBudgetDropdownHover] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const usersApi = new ApiRequest(API_ROUTES.USERS, API_AUTH_TYPES.USER);
  const { data: linkData } = useQuery({
    queryFn: usersApi.getFn({ endpoint: "calendly_link" }),
    queryKey: "calendlyLink",
  });


  const [calendlyLink, setCalendlyLink] = useState('');
  const householdsApi = new ApiRequest(API_ROUTES.HOUSEHOLDS, API_AUTH_TYPES.CLIENT);
  const advisorHouseholdsApi = new ApiRequest(API_ROUTES.HOUSEHOLDS, API_AUTH_TYPES.ADVISOR);

  // Check household access for advisors viewing specific households
  const { data: accessData, isLoading: accessLoading } = useQuery({
    queryFn: advisorHouseholdsApi.getFn({
      endpoint: "has_access",
      params: [safeConfigHouseholdId]
    }),
    queryKey: ["household-access", safeConfigHouseholdId],
    enabled: safeIsAdvisor && safeConfigHouseholdId && safeConfigHouseholdId !== 'me',
    onSuccess: (data) => {
      if (data?.user_has_access === false) {
        nav('/crm/dashboard');
      }
    },
    onError: () => {
      nav('/crm/dashboard');
    }
  });

  const youvestApi = new ApiRequest(API_ROUTES.YOUVEST, API_AUTH_TYPES.USER);
  const { data: youvestOnboardingData, isLoading: youvestLoading } = useQuery({
    queryFn: youvestApi.getFn({ endpoint: "onboarding" }),
    queryKey: "youvest-onboarding-completed",
  });

  // Fetch net worth budget access
  const { data: netWorthBudgetAccessData, isLoading: netWorthBudgetLoading } = useQuery({
    queryFn: householdsApi.getFn({ endpoint: "net_worth_budget_access", queryParams: { household_id: safeConfigHouseholdId } }),
    queryKey: `net-worth-budget-access-${safeConfigHouseholdId}`,
  });

  // Fetch YouVest access
  const { data: youvestAccessData, isLoading: youvestAccessLoading } = useQuery({
    queryFn: householdsApi.getFn({ endpoint: "youvest_access", queryParams: { household_id: safeConfigHouseholdId } }),
    queryKey: `youvest-access-${safeConfigHouseholdId}`,
  });

  const accessToken = useSelector((state) => state.user.accessToken);

  // Function to create navigation with current YouVest and Net Worth Budget status
  const createNavigationWithAccess = useCallback((hasYouvestAccess, hasNetWorthBudgetAccess) => {
    // Get the base path for advisor routes
    const getBaseHref = (path) => {
      if (safeIsAdvisor && safeConfigHouseholdId && safeConfigHouseholdId !== 'me') {
        return `/crm/households/${safeConfigHouseholdId}${path}`;
      }
      return path;
    };


    // Special handling for YouVest routes
    const getYouvestHref = (path) => {
      if (safeIsAdvisor && safeConfigHouseholdId && safeConfigHouseholdId !== 'me') {
        // Remove leading slash from path for YouVest
        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        return `/crm/youvest/${safeConfigHouseholdId}/${cleanPath}`;
      }
      return `youvest${path}`;
    };

    const baseNavigation = [
      ...(safeIsAdvisor
        ? [
          {
            name: "Advisor",
            href: getBaseHref("/advisor"),
            icon: LightningBoltIcon,
            current: window.location.href.includes("/advisor"),
          },
        ]
        : []),
      {
        name: "Dashboard",
        href: getBaseHref("/dashboard"),
        icon: HomeIcon,
        current: window.location.href.includes("/dashboard") &&
          !window.location.href.includes("/admin") &&
          !window.location.href.includes("/advisor"),
      },
      // Only include Net Worth if they have access
      ...(hasNetWorthBudgetAccess ? [{
        name: "Net Worth",
        href: getBaseHref("/net-worth/Summary"),
        icon: ChartBarIcon,
        current: window.location.href.includes("/net-worth"),
      }] : []),
      // Only include Budget if they have access
      ...(hasNetWorthBudgetAccess ? [{
        name: "Budget",
        icon: CreditCardIcon,
        current: window.location.href.includes("/transactions") || window.location.href.includes("/spending") || window.location.href.includes("/budgets"),
        children: [

          {
            name: "Spending",
            href: getBaseHref("/spending/Overview"),
            icon: CurrencyDollarIcon,
            current: window.location.href.includes("/spending"),
          },
          {
            name: "Transactions",
            href: getBaseHref("/transactions"),
            icon: SearchIcon,
            current: window.location.href.includes("/transactions"),
          },
        ]
      }] : []),
      // Only include YouVest if they have access
      ...(hasYouvestAccess ? [{
        name: "YouVest",
        href: youvestOnboardingData?.completed ? getYouvestHref("/My Portfolio") : "/youvest-onboarding",
        target: "",
        rel: "",
        icon: ChartPieIcon,
        current: window.location.href.includes("/youvest"),
      }] : []),
      {
        name: "Financial Story",
        href: getBaseHref("/financial-story"),
        target: "",
        rel: "",
        icon: BookOpenIcon,
        current: window.location.href.includes("/financial-story"),
      },
      {
        name: "Notes",
        href: getBaseHref("/notes"),
        target: "",
        rel: "",
        icon: ClipboardListIcon,
        current: window.location.href.includes("/notes"),
      },
      {
        name: 'Vault',
        href: getBaseHref('/vault'),
        target: '',
        rel: '',
        icon: FolderIconOutline,
        current: window.location.href.includes('/vault'),
      },
      {
        name: "Learning Lab",
        href: getResourcesHref("/learning_lab"),
        target: "",
        rel: "",
        icon: AcademicCapIcon,
        current: window.location.href.includes("/learning_lab"),
      },
      {
        name: "Settings",
        href: getBottomNavHref("/settings"),
        target: "",
        rel: "",
        icon: CogIcon,
        current: window.location.href.includes("/settings"),
      },

    ];
    return baseNavigation;
  }, [youvestOnboardingData, safeIsAdvisor, safeConfigHouseholdId]);



  const externalLinks = [



  ];

  const advisor = [
    {
      name: "Bun",
      href: "/crm/dashboard",
      icon: TbBread,
      current: window.location.href.includes("/advisor"),
    },
  ];



  const userNavigation = [
    {
      name: "Sign out",
      func: () => {
        try {
          nav("/");
          setTimeout(() => {
            logout();
          }, 1000);
        } catch (error) {
          console.error('Error signing out:', error);
        }
      },
    },

  ];

  const legal = [
    {
      name: "Form CRS",
      href: 'https://reports.adviserinfo.sec.gov/crs/crs_304612.pdf',
      target: "_blank",
      rel: "noopener noreferrer",
      current: false,
    },
    {
      name: "Privacy Policy",
      href: "https://drive.google.com/file/d/1wll2KkNkfLGgaRUUW7yr0PQB3EpDang5/view?usp=drive_link",
      target: "_blank",
      rel: "noopener noreferrer",
      current: false,
    },

    {
      name: "ADV Part 2A",
      href: 'https://files.adviserinfo.sec.gov/IAPD/Content/Common/crd_iapd_Brochure.aspx?BRCHR_VRSN_ID=943005',
      target: "_blank",
      rel: "noopener noreferrer",
      current: false,
    },
    {
      name: "Terms of Use",
      href: "/legal/terms_of_use",
      target: "_blank",
      rel: "noopener noreferrer",
      current: false,
    },
    {
      name: "Security",
      href: "/security",
      target: "",
      rel: "",
      current: window.location.href.includes("/security"),
    },
    {
      name: "Additional Terms",
      href: "/legal/additional_terms",
      target: "_blank",
      rel: "noopener noreferrer",
      current: false,
    },
    {
      name: "Notice & Disclaimer",
      href: "/legal/notice_and_disclamer",
      target: "_blank",
      rel: "noopener noreferrer",
      current: false,
    },
  ];

  const topLevelLegal = [
  ];

  const getResourcesHref = (path) => {
    if (safeIsAdvisor && safeConfigHouseholdId && safeConfigHouseholdId !== 'me') {
      return `/crm/households/${safeConfigHouseholdId}${path}`;
    }
    return path;
  };

  const resources = [

    {
      name: 'Partners',
      href: getResourcesHref('/partners'),
      target: '',
      rel: '',
      icon: BriefcaseIcon,
      current: window.location.href.includes('/partners'),
    },

    {
      name: "Abundo's Blog",
      icon: ChatAltIcon,
      target: "_blank",
      rel: "noopener noreferrer",
      href: "/blog",
    },
    {
      name: "Newsletter",
      href: "https://abundowealth.beehiiv.com/",
      target: "_blank",
      rel: "noopener noreferrer",
      icon: NewspaperIcon,
      current: false,
    },
    {
      name: "RightCapital",
      target: "_blank",
      rel: "noopener noreferrer",
      href: "https://app.rightcapital.com/account/login",
      icon: RightCapitalIcon,
      current: false,
    },

  ];

  const getBottomNavHref = (path) => {
    if (safeIsAdvisor && safeConfigHouseholdId && safeConfigHouseholdId !== 'me') {
      return `/crm/households/${safeConfigHouseholdId}${path}`;
    }
    return path;
  };

  const bottomNav = [
  ];

  // Add Settings, external links, and Abundo Love Corner to legal array after getBottomNavHref is defined
  const legalWithLoveCorner = [


    ...legal,
    {
      name: "",
      href: getBottomNavHref("/abundo-love-corner"),
      target: "",
      rel: "",
      icon: HeartIcon,
      current: window.location.href.includes("/abundo-love-corner"),
    },
  ];

  // Handle hover with delay
  const handleBudgetHoverEnter = useCallback(() => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setBudgetDropdownHover(true);
    }, 150); // 150ms delay
    setHoverTimeout(timeout);
  }, [hoverTimeout]);

  const handleBudgetHoverLeave = useCallback(() => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setBudgetDropdownHover(false);
  }, [hoverTimeout]);

  const authenticateAdminAdvisor = useCallback(async () => {
    try {
      setIsLoading(true);
      const auth = await authenticateAdvisorAdmin(accessToken);

      setIsManager(auth.is_manager);
      setUserId(auth.user_id);

      // Navigation will be set by the useEffect when access data loads
    } catch (error) {
      console.error(error);
      setTimeout(() => {
        nav("/");
        setTimeout(() => {
          logout();
        }, 1000);
      }, 100);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, logout]);

  // Effect to handle initial authentication
  useEffect(() => {
    authenticateAdminAdvisor();
  }, []);

  // Effect to update navigation when access status changes
  useEffect(() => {
    if (!isLoading && !youvestLoading && !netWorthBudgetLoading && !youvestAccessLoading) {
      const updatedNavigation = createNavigationWithAccess(
        youvestAccessData?.youvest_access,
        netWorthBudgetAccessData?.net_worth_budget_access
      );
      setFilteredNavigation(updatedNavigation);
    }
  }, [isLoading, youvestLoading, netWorthBudgetLoading, youvestAccessLoading, youvestAccessData, netWorthBudgetAccessData, createNavigationWithAccess]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  useEffect(() => {
    if (linkData) {
      setCalendlyLink(linkData);
    }
  }, [linkData]);

  if (isLoading || youvestLoading || netWorthBudgetLoading || youvestAccessLoading || (safeIsAdvisor && safeConfigHouseholdId && safeConfigHouseholdId !== 'me' && accessLoading)) {
    return <RouteLoadingSpinner />;
  }

  // Check if advisor has access to the household (for private households)
  if (safeIsAdvisor && safeConfigHouseholdId && safeConfigHouseholdId !== 'me' && accessData?.user_has_access === false) {
    nav('/crm/dashboard');
    return <RouteLoadingSpinner />;
  }

  if (safeIsAdvisor) {
    return (
      <>
        {/* Advisor-specific components */}
        <ParabotInterface />
        <ReminderButton />
        <EmailMessages />
        <Toasts />

        <AdvisorLayout isManager={isManager} userId={userId} clientNavProps={{ nav, filteredNavigation, externalLinks, resources, bottomNav, topLevelLegal, legal: legalWithLoveCorner, isAdvisor: safeIsAdvisor, advisor, userNavigation, calendlyLink, setSidebarOpen, meetingMode, setMeetingMode }} useContainer={useContainer} bgColor={bgColor}>{children}</AdvisorLayout>
      </>
    );
  }

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 flex z-40 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex-1 flex flex-col max-w-sm w-full pt-4 pb-4 bg-white">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <a
                  href="/dashboard"
                  className=" flex-shrink-0 flex items-center px-4"
                >
                  <img className="h-8 w-auto" src={abundoLogo} alt="abundo" />{" "}
                </a>
                <div className="mt-4 flex-1 overflow-y-auto">
                  <nav className="px-3 space-y-2">
                    {filteredNavigation && filteredNavigation.map((item) =>
                      item.children ? (
                        <div
                          key={item.name}
                          onMouseEnter={() => item.name === "Budget" && handleBudgetHoverEnter()}
                          onMouseLeave={() => item.name === "Budget" && handleBudgetHoverLeave()}
                        >
                          <Disclosure as="div" className="space-y-1" defaultOpen={item.current || (item.name === "Budget" && budgetDropdownHover)}>
                            {({ open }) => (
                              <>
                                <Disclosure.Button
                                  className={classNames(
                                    item.current
                                      ? "bg-gray-100 text-gray-900"
                                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                    "group flex items-center w-full px-3 py-3 text-base font-medium rounded-md min-h-[44px]"
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current
                                        ? "text-gray-500"
                                        : "text-gray-400 group-hover:text-gray-500",
                                      "mr-4 flex-shrink-0 h-6 w-6"
                                    )}
                                    aria-hidden="true"
                                  />
                                  <span className="flex-1 text-left">{item.name}</span>
                                  <ChevronDownIcon
                                    className={`${open || (item.name === "Budget" && budgetDropdownHover) ? 'transform rotate-180' : ''
                                      } w-5 h-5 text-gray-400 transition-transform duration-200`}
                                  />
                                </Disclosure.Button>
                                <Transition
                                  show={open || (item.name === "Budget" && budgetDropdownHover)}
                                  enter="transition duration-200 ease-out"
                                  enterFrom="transform scale-95 opacity-0"
                                  enterTo="transform scale-100 opacity-100"
                                  leave="transition duration-150 ease-in"
                                  leaveFrom="transform scale-100 opacity-100"
                                  leaveTo="transform scale-95 opacity-0"
                                >
                                  <Disclosure.Panel className="px-3 pt-2 pb-2 space-y-2" static={item.name === "Budget" && budgetDropdownHover}>
                                    {item.children.map((subItem) => (
                                      <a
                                        key={subItem.name}
                                        href={subItem.href}
                                        className={classNames(
                                          subItem.current
                                            ? "bg-gray-200 text-gray-900"
                                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                          "group flex items-center w-full pl-7 pr-3 py-3 text-base font-medium rounded-md min-h-[44px]"
                                        )}
                                      >
                                        {subItem.icon && (
                                          <subItem.icon
                                            className="mr-4 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                            aria-hidden="true"
                                          />
                                        )}
                                        {subItem.name}
                                      </a>
                                    ))}
                                  </Disclosure.Panel>
                                </Transition>
                              </>
                            )}
                          </Disclosure>
                        </div>
                      ) : (
                        <button
                          key={item.name}
                          className="w-full"

                        >
                          <a
                            key={item.name}
                            href={item.href}
                            rel={item.rel}
                            target={item.target}
                            className={classNames(
                              item.current
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                              "group flex items-center w-full px-3 py-3 text-base font-medium rounded-md min-h-[44px]"
                            )}
                          >
                            <item.icon
                              className={classNames(
                                item.current
                                  ? "text-gray-500"
                                  : "text-gray-400 group-hover:text-gray-500",
                                "mr-4 flex-shrink-0 h-6 w-6"
                              )}
                              aria-hidden="true"
                            />
                            {item.name}
                          </a>
                        </button>
                      )
                    )}


                    <Disclosure defaultOpen={false}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex justify-between w-full px-3 py-3 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md min-h-[44px]">
                            <span className="flex items-center">
                              <LibraryIcon
                                className="mr-4 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-500"
                                aria-hidden="true"
                              />
                              Resources
                            </span>
                            <ChevronDownIcon
                              className={`${open ? 'transform rotate-180' : ''
                                } w-5 h-5 text-gray-400`}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel className="px-3 pt-2 pb-2 space-y-2">
                            {resources.map((item) => (
                              item.target ? (
                                <a
                                  key={item.name}
                                  href={item.href}
                                  rel={item.rel}
                                  target={item.target}
                                  className={classNames(
                                    item.current
                                      ? "bg-gray-100 text-gray-900"
                                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                    "group flex items-center w-full px-3 py-3 text-base font-medium rounded-md min-h-[44px]"
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current
                                        ? "text-gray-500"
                                        : "text-gray-400 group-hover:text-gray-500",
                                      "mr-4 flex-shrink-0 h-6 w-6"
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </a>
                              ) : (
                                <button
                                  key={item.name}
                                  onClick={() => nav(item.href)}
                                  className={classNames(
                                    item.current
                                      ? "bg-gray-100 text-gray-900"
                                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                    "group flex items-center w-full px-3 py-3 text-base font-medium rounded-md min-h-[44px]"
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current
                                        ? "text-gray-500"
                                        : "text-gray-400 group-hover:text-gray-500",
                                      "mr-4 flex-shrink-0 h-6 w-6"
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </button>
                              )
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                    <Disclosure defaultOpen={false}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex justify-between w-full px-3 py-3 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md min-h-[44px]">
                            <span className="flex items-center">
                              <span className="ml-10">More</span>
                            </span>
                            <ChevronDownIcon
                              className={`${open ? 'transform rotate-180' : ''
                                } w-5 h-5 text-gray-400`}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel className="px-3 pt-2 pb-2 space-y-2">
                            {legalWithLoveCorner.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                rel={item.rel}
                                target={item.target}
                                className={classNames(
                                  item.current
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                  "group flex items-center w-full px-3 py-3 text-base font-medium rounded-md min-h-[44px]"
                                )}
                              >
                                {item.icon && (
                                  <item.icon
                                    className={classNames(
                                      item.current
                                        ? "text-gray-500"
                                        : "text-gray-400 group-hover:text-gray-500",
                                      "mr-4 flex-shrink-0 h-6 w-6"
                                    )}
                                    aria-hidden="true"
                                  />
                                )}
                                {item.name}
                              </a>
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>





                    {safeIsAdvisor &&
                      advisor.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          rel={item.rel}
                          target={item.target}
                          className={classNames(
                            item.current
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center px-2 py-2 text-base font-medium rounded-md"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-gray-500"
                                : "text-gray-400 group-hover:text-gray-500",
                              "mr-4 flex-shrink-0 h-6 w-6"
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </a>
                      ))}
                  </nav>



                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:flex lg:w-52 lg:flex-col lg:fixed lg:inset-y-0">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex flex-col flex-grow border-r  pt-5 bg-white overflow-y-auto">
            <a
              className="flex items-center justify-center flex-shrink-0 px-4"
              href="/dashboard"
            >
              <img className="h-14 w-auto" src={abundoLogo} alt="abundo" />
            </a>
            <div className="mt-5 flex-grow flex flex-col">
              <div>
                {" "}
                <nav className="flex-1 pb-4 space-y-1  mx-2">
                  {" "}
                  {filteredNavigation && filteredNavigation.map((item) =>
                    item.children ? (
                      <div
                        key={item.name}
                        onMouseEnter={() => item.name === "Budget" && handleBudgetHoverEnter()}
                        onMouseLeave={() => item.name === "Budget" && handleBudgetHoverLeave()}
                      >
                        <Disclosure as="div" defaultOpen={item.current || (item.name === "Budget" && budgetDropdownHover)}>
                          {({ open }) => (
                            <>
                              <Disclosure.Button
                                className={classNames(
                                  item.current
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                                  'group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md'
                                )}
                              >
                                <item.icon
                                  className={classNames(
                                    item.current ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                                    'mr-3 h-6 w-6 flex-shrink-0'
                                  )}
                                  aria-hidden="true"
                                />
                                <span className="flex-1 text-left">{item.name}</span>
                                <ChevronDownIcon
                                  className={`${open || (item.name === "Budget" && budgetDropdownHover) ? 'transform rotate-180' : ''} w-5 h-5 text-gray-400 transition-transform duration-200`}
                                />
                              </Disclosure.Button>
                              <Transition
                                show={open || (item.name === "Budget" && budgetDropdownHover)}
                                enter="transition duration-200 ease-out"
                                enterFrom="transform scale-95 opacity-0"
                                enterTo="transform scale-100 opacity-100"
                                leave="transition duration-150 ease-in"
                                leaveFrom="transform scale-100 opacity-100"
                                leaveTo="transform scale-95 opacity-0"
                              >
                                <Disclosure.Panel className="space-y-1 pl-5" static={item.name === "Budget" && budgetDropdownHover}>
                                  {item.children.map((subItem) => (
                                    <button
                                      key={subItem.name}
                                      onClick={() => nav(subItem.href)}
                                      className={classNames(
                                        subItem.current
                                          ? 'bg-gray-100 text-gray-900'
                                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                                        'group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md'
                                      )}
                                    >
                                      {subItem.icon && (
                                        <subItem.icon
                                          className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                          aria-hidden="true"
                                        />
                                      )}
                                      {subItem.name}
                                    </button>
                                  ))}
                                </Disclosure.Panel>
                              </Transition>
                            </>
                          )}
                        </Disclosure>
                      </div>
                    ) : (
                      <button
                        key={item.name}
                        onClick={() => nav(item.href)}
                        className={classNames(
                          item.current
                            ? "bg-gray-100 text-gray-900"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                          "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md"
                        )}


                      >

                        <item.icon
                          className={classNames(
                            item.current
                              ? "text-gray-500"
                              : "text-gray-400 group-hover:text-gray-500",
                            "mr-3 flex-shrink-0 h-6 w-6"
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </button>
                    )
                  )}{" "}

                  <Disclosure defaultOpen={false}>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex justify-between w-full px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md">
                          <span className="flex items-center">
                            <LibraryIcon
                              className="mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-500"
                              aria-hidden="true"
                            />
                            Resources
                          </span>
                          <ChevronDownIcon
                            className={`${open ? 'transform rotate-180' : ''
                              } w-5 h-5 text-gray-400`}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel className="px-2 pt-2 pb-2 space-y-1">
                          {resources.map((item) => (
                            item.target ? (
                              <a
                                key={item.name}
                                href={item.href}
                                rel={item.rel}
                                target={item.target}
                                className={classNames(
                                  item.current
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                  "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                                )}
                              >
                                <item.icon
                                  className={classNames(
                                    item.current
                                      ? "text-gray-500"
                                      : "text-gray-400 group-hover:text-gray-500",
                                    "mr-3 flex-shrink-0 h-6 w-6"
                                  )}
                                  aria-hidden="true"
                                />
                                {item.name}
                              </a>
                            ) : (
                              <button
                                key={item.name}
                                onClick={() => nav(item.href)}
                                className={classNames(
                                  item.current
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                  "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md"
                                )}
                              >
                                <item.icon
                                  className={classNames(
                                    item.current
                                      ? "text-gray-500"
                                      : "text-gray-400 group-hover:text-gray-500",
                                    "mr-3 flex-shrink-0 h-6 w-6"
                                  )}
                                  aria-hidden="true"
                                />
                                {item.name}
                              </button>
                            )
                          ))}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>


                  <Disclosure defaultOpen={false}>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex justify-between w-full px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md">
                          <span className="flex items-center">
                            <span className="ml-9">More</span>
                          </span>
                          <ChevronDownIcon
                            className={`${open ? 'transform rotate-180' : ''
                              } w-5 h-5 text-gray-400`}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel className="px-2 pt-2 pb-2 space-y-1">
                          {legalWithLoveCorner.map((item) => (
                            <a
                              key={item.name}
                              href={item.href}
                              rel={item.rel}
                              target={item.target}
                              className={classNames(
                                item.current
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                              )}
                            >
                              {item.icon && (
                                <item.icon
                                  className={classNames(
                                    item.current
                                      ? "text-gray-500"
                                      : "text-gray-400 group-hover:text-gray-500",
                                    "mr-3 flex-shrink-0 h-6 w-6"
                                  )}
                                  aria-hidden="true"
                                />
                              )}
                              {item.name}
                            </a>
                          ))}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>

                  {safeIsAdvisor &&
                    advisor.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        rel={item.rel}
                        target={item.target}
                        className={classNames(
                          item.current
                            ? "bg-gray-100 text-gray-900"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                          "group flex items-center px-2 py-2 text-base font-medium rounded-md"
                        )}
                      >
                        <item.icon
                          className={classNames(
                            item.current
                              ? "text-gray-500"
                              : "text-gray-400 group-hover:text-gray-500",
                            "mr-4 flex-shrink-0 h-6 w-6"
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </a>
                    ))}
                </nav>{" "}
              </div>


            </div>
          </div>
        </div>

        {/* End Static sidebar for desktop */}
        <div className=" lg:pl-52 flex flex-col flex-1">
          {topNavbarType === 'standard' && topNavBar({ setSidebarOpen, calendlyLink, userNavigation, nav, meetingMode, setMeetingMode })}

          {useContainer ? <main
            className={`flex-1 justify-center min-h-screen relative px-3 sm:px-6 lg:px-8 xl:px-10 overflow-x-hidden  ${bgColor ? bgColor : " bg-c-midnight-50"
              }  pt-7`}
          >
            <div className="max-w-screen-2xl mx-auto overflow-x-hidden pb-8 rounded-lg">
              <PageHeader isAdvisor={safeIsAdvisor} />
              {children}
            </div>
          </main> : <div className="max-w-screen-2xl mx-auto overflow-x-hidden pb-8 rounded-lg">
            <PageHeader isAdvisor={safeIsAdvisor} />
            {children}
          </div>}
        </div>
      </div>
    </>
  );
}



function topNavBar({ setSidebarOpen, calendlyLink, userNavigation, nav, meetingMode, setMeetingMode }) {
  return (
    <div className="sticky lg:relative top-0 z-[45] flex-shrink-0 flex h-12 bg-c-orange-abundo shadow">
      <button
        type="button"
        className="px-4 border-r border-gray-200 bg-white text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-c-orange-abundo lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
      </button>
      <div className="flex-1 px-4 flex justify-end">
        <div className=" flex items-center lg:ml-6 space-x-1">
          <Notifications />
          <button
            className="  p-1 rounded-full hover:bg-white transition duration-200 "
            onClick={() => nav('/vault')}
          >
            <span className="sr-only">Documents</span>
            <FolderIcon
              className="h-6 w-6 text-white hover:text-gray-400 transition duration-200"
              aria-hidden="true"
            />
          </button>
          <button
            className="w-full flex items-center"

          >
            <a
              type="button"
              href={calendlyLink}
              target="_blank"
              rel="noopener noreferrer"
              className="  p-1 rounded-full hover:bg-white transition duration-200 "
            >
              <span className="sr-only">View notifications</span>

              <CalendarIcon
                className="h-6 w-6 text-white hover:text-gray-400 transition duration-200"
                aria-hidden="true"
              />
            </a>
          </button>
          {/* Profile dropdown */}
          <Menu as="div" className="ml-3 relative">
            <div className="">
              <Menu.Button className=" p-1 rounded-full hover:bg-white transition duration-300 ">
                <span className="sr-only">Open user menu</span>
                <UserIcon
                  className="h-6 w-6 text-white hover:text-gray-400 transition duration-300"
                  aria-hidden="true"
                />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                {userNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <button
                        className={classNames(
                          active ? "bg-gray-100" : "",
                          "block px-4 py-2 text-sm text-gray-700 w-full text-left"
                        )}
                        onClick={() => {
                          item.func();
                        }}
                      >
                        {item.name}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  )
}

function AdvisorLayout({ children, isManager, userId, clientNavProps, useContainer, bgColor }) {
  const { householdId, isAdvisor } = useConfig();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHiddenLabels, setShowHiddenLabels] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [budgetDropdownHover, setBudgetDropdownHover] = useState(false);
  const [advisorHoverTimeout, setAdvisorHoverTimeout] = useState(null);

  // Handle hover with delay for advisor layout
  const handleAdvisorBudgetHoverEnter = useCallback(() => {
    if (advisorHoverTimeout) {
      clearTimeout(advisorHoverTimeout);
    }
    const timeout = setTimeout(() => {
      setBudgetDropdownHover(true);
    }, 150); // 150ms delay
    setAdvisorHoverTimeout(timeout);
  }, [advisorHoverTimeout]);

  const handleAdvisorBudgetHoverLeave = useCallback(() => {
    if (advisorHoverTimeout) {
      clearTimeout(advisorHoverTimeout);
      setAdvisorHoverTimeout(null);
    }
    setBudgetDropdownHover(false);
  }, [advisorHoverTimeout]);

  const [meetingMode, setMeetingMode] = useState(() => {
    try {
      const itemStr = localStorage.getItem(`meetingMode_${householdId}`);
      if (!itemStr) return false;
      const item = JSON.parse(itemStr);
      const now = new Date();
      if (now.getTime() > item.expiry) {
        localStorage.removeItem(`meetingMode_${householdId}`);
        return false;
      }
      return item.value;
    } catch (error) {
      console.error("Error reading from localStorage", error);
      return false;
    }
  });

  useEffect(() => {
    try {
      if (meetingMode) {
        const now = new Date();
        const item = {
          value: true,
          expiry: now.getTime() + 24 * 60 * 60 * 1000, // 24 hours
        };
        localStorage.setItem(`meetingMode_${householdId}`, JSON.stringify(item));
      } else {
        localStorage.removeItem(`meetingMode_${householdId}`);
      }
    } catch (error) {
      console.error("Error writing to localStorage", error);
    }
  }, [meetingMode, householdId]);

  useEffect(() => {
    try {
      const itemStr = localStorage.getItem(`meetingMode_${householdId}`);
      let value = false;
      if (itemStr) {
        const item = JSON.parse(itemStr);
        const now = new Date();
        if (now.getTime() < item.expiry) {
          value = item.value;
        } else {
          localStorage.removeItem(`meetingMode_${householdId}`);
        }
      }
      if (value !== meetingMode) {
        setMeetingMode(value);
      }
    } catch (error) {
      console.error("Error reading from localStorage", error);
      setMeetingMode(false);
    }
  }, [householdId]);

  // Cleanup timeout on unmount for advisor layout
  useEffect(() => {
    return () => {
      if (advisorHoverTimeout) {
        clearTimeout(advisorHoverTimeout);
      }
    };
  }, [advisorHoverTimeout]);

  useEffect(() => {
    const hiddenItems = [...advisorNav(userId), ...adminNav, ...managerNav, ...contentNav].filter(item => item.hidden);
    const anyHiddenCurrent = hiddenItems.some(item => item.current);
    if (anyHiddenCurrent) {
      setShowHiddenLabels(true);
    }
  }, [userId]);

  return (
    <div>
      {!meetingMode && <AdvisorMobileSidebar advisor={advisorNav(userId)} admin={adminNav} manager={managerNav} isManager={isManager} content={contentNav} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} clientNavProps={clientNavProps} />}

      {/* Advisor Primary Sidebar */}
      {!meetingMode &&
        <div
          onMouseEnter={() => setIsSidebarHovered(true)}
          onMouseLeave={() => setIsSidebarHovered(false)}
          className={classNames(
            "hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:flex-col transition-all duration-300",
            isSidebarHovered ? "lg:w-56" : "lg:w-20"
          )}
        >
          <AdvisorDesktopSidebar
            advisor={advisorNav(userId)}
            admin={adminNav}
            manager={managerNav}
            content={contentNav}
            isManager={isManager}
            showHiddenLabels={showHiddenLabels}
            setShowHiddenLabels={setShowHiddenLabels}
            isSidebarHovered={isSidebarHovered}
          />
        </div>
      }

      {/* Client Secondary Sidebar */}
      <div className={classNames(
        "hidden lg:flex lg:w-52 lg:flex-col lg:fixed lg:inset-y-0 lg:z-40 transition-all duration-300",
        meetingMode ? 'lg:left-0' : (isSidebarHovered ? "lg:left-56" : "lg:left-20")
      )}>
        <ClientDesktopSidebar clientNavProps={{ ...clientNavProps, budgetDropdownHover, handleBudgetHoverEnter: handleAdvisorBudgetHoverEnter, handleBudgetHoverLeave: handleAdvisorBudgetHoverLeave }} />
      </div>

      <div className={classNames(
        "flex flex-col flex-1 transition-all duration-300",
        meetingMode ? 'lg:pl-52' : (isSidebarHovered ? "lg:pl-[27rem]" : "lg:pl-[18rem]")
      )}>
        <AdvisorTopBar setSidebarOpen={setSidebarOpen} meetingMode={meetingMode} setMeetingMode={setMeetingMode} />
        {useContainer ? (
          <main
            className={`flex-1 justify-center min-h-screen relative px-2 sm:px-4 lg:px-8 xl:px-10 overflow-x-hidden ${bgColor ? bgColor : " bg-c-midnight-50"} pt-7`}
          >
            <div className="max-w-screen-2xl mx-auto overflow-x-hidden pb-8 rounded-lg">
              <PageHeader isAdvisor={isAdvisor} />
              {children}
            </div>
          </main>
        ) : (
          <>
            <PageHeader isAdvisor={isAdvisor} />
            {children}
          </>
        )}
      </div>
    </div>
  );
}

const advisorNav = (userId) => [
  {
    name: "Dashboard",
    href: "/crm/dashboard",
    icon: {
      outline: TbLayoutDashboard,
      solid: TbLayoutDashboard
    },
    current: window.location.href.includes("crm/dashboard"),
  },
  {
    name: "BunMail",
    href: "/crm/emails?label=inbox&page=1&client_only=false",
    icon: {
      outline: MailIcon,
      solid: MailIconSolid
    },
    current: window.location.href.includes("/crm/emails"),
  },
  {
    name: "Households",
    href: "/crm/households/smart-table/default",
    icon: {
      outline: UsersIcon,
      solid: UsersIconSolid
    },
    current: window.location.href.includes("/households"),
  },
  {
    name: "Users",
    href: "/crm/users/smart-table/default",
    icon: {
      outline: UserIcon,
      solid: UserIconSolid
    },
    current: window.location.href.includes("/users"),
  },
  {
    name: "Prospects",
    href: "/crm/prospects/smart-table/default",
    icon: {
      outline: EmojiHappyIcon,
      solid: EmojiHappyIconSolid
    },
    current: window.location.href.includes("/prospects"),
  },
  {
    name: "Onboarding",
    href: "/crm/onboarding/smart-table/default",
    icon: {
      outline: LoginIcon,
      solid: LoginIconSolid
    },
    current: window.location.href.includes("/onboarding"),
  },
  {
    name: "Meetings",
    href: "/crm/meetings/smart-table/default",
    icon: {
      outline: PresentationChartBarIcon,
      solid: PresentationChartBarIconSolid
    },
    current: window.location.href.includes("/meetings"),
  },
  {
    name: "Notifications",
    href: "/crm/notifications/smart-table/default",
    icon: {
      outline: BellIcon,
      solid: BellIconSolid
    },
    current: window.location.href.includes("/notifications"),
    hidden: true
  },
  {
    name: "Forms",
    href: "/crm/forms/smart-table/default",
    icon: {
      outline: DocumentTextIcon,
      solid: DocumentTextIconSolid
    },
    current: window.location.href.includes("/forms"),
    hidden: true
  },
  {
    name: "Resources",
    href: "/crm/resources",
    icon: {
      outline: BookOpenIcon,
      solid: BookOpenIconSolid
    },
    current: window.location.href.includes("/resources"),
  },
  {
    name: "Settings",
    href: `/crm/advisor/settings/${userId}`,
    icon: {
      outline: CogIcon,
      solid: CogIconSolid
    },
    current: window.location.href.includes("/advisor/settings"),
    hidden: true
  },
];

const adminNav = [
  {
    name: "Analytics",
    href: "/crm/analytics",
    icon: {
      outline: DatabaseIcon,
      solid: DatabaseIconSolid
    },
    current: window.location.href.includes("/analytics"),
  },
];

const managerNav = [
  {
    name: "M Dashboard",
    href: "/crm/manager/dashboard",
    icon: {
      outline: TbLayoutDashboard,
      solid: TbLayoutDashboard
    },
    current: window.location.href.includes("/manager/dashboard"),
  },
];

const contentNav = [
  {
    name: "Blog",
    href: "/content/blog/dashboard",
    icon: {
      outline: AnnotationIcon,
      solid: AnnotationIconSolid
    },
    current: window.location.href.includes("/blog"),
  },
  {
    name: "Learning Lab",
    href: "/content/learning_lab/dashboard",
    icon: {
      outline: AcademicCapIcon,
      solid: AcademicCapIcon
    },
    current: window.location.href.includes("/learning_lab"),
  },
];

function AdvisorMobileSidebar({ advisor, admin, manager, content, isManager, sidebarOpen, setSidebarOpen, clientNavProps }) {
  const { filteredNavigation, externalLinks, resources, bottomNav, nav } = clientNavProps;
  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 flex z-40 lg:hidden"
        onClose={setSidebarOpen}
        id="mobile-sidebar-dialog"
        aria-labelledby="mobile-sidebar-dialog-title"
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" id="mobile-sidebar-overlay" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white">
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                  id="mobile-sidebar-close-button"
                  aria-label="Close sidebar"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XIcon
                    className="h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </Transition.Child>
            <button
              onClick={() => nav("/crm/households")}
              className="flex-shrink-0 flex items-center px-4"
              id="mobile-sidebar-logo-button"
              aria-label="Navigate to CRM Households"
            >
              <img className="h-12 px-4 w-auto" src={abundoLogo} alt="abundo" />
            </button>
            <div className="mt-5 flex-1 overflow-y-auto text-sm">
              <nav className="px-2 space-y-1 list-none">
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400" aria-label="Advisor Options">Advisor</div>
                  {advisor.map((item) => (
                    <AdvisorNavLink key={item.name} item={item} isSidebarHovered={true} />
                  ))}
                </li>
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400" aria-label="Miscellaneous Options">Misc</div>
                  {admin.map((item) => (
                    <AdvisorNavLink key={item.name} item={item} isSidebarHovered={true} />
                  ))}
                </li>
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400" aria-label="Content Options">Content</div>
                  {content.map((item) => (
                    <AdvisorNavLink key={item.name} item={item} isSidebarHovered={true} />
                  ))}
                </li>
                {isManager && (
                  <li>
                    <div className="text-xs font-semibold leading-6 text-gray-400" aria-label="Manager Options">Manager</div>
                    {manager.map((item) => (
                      <AdvisorNavLink key={item.name} item={item} isSidebarHovered={true} />
                    ))}
                  </li>
                )}
                <div className="border-t my-4"></div>
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400" aria-label="Client Options">Client View</div>
                  {filteredNavigation.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => nav(item.href)}
                      className={classNames(
                        item.current
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                        "group flex items-center w-full px-2 py-2 text-base font-medium rounded-md"
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.current
                            ? "text-gray-500"
                            : "text-gray-400 group-hover:text-gray-500",
                          "mr-4 flex-shrink-0 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </button>
                  ))}
                </li>
              </nav>
            </div>
          </div>
        </Transition.Child>
        <div className="flex-shrink-0 w-14" aria-hidden="true">
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function AdvisorDesktopSidebar({ advisor, admin, manager, content, isManager, showHiddenLabels, setShowHiddenLabels, isSidebarHovered }) {
  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-slate-200 bg-slate-100 px-2 sm:px-4 xl:px-6 pb-4 pt-5">
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-4">
          <li>
            <ul className="-mx-2 space-y-1">
              {advisor.filter(a => !a.hidden).map((item) => (
                <li key={item.name}>
                  <AdvisorNavLink item={item} isSidebarHovered={isSidebarHovered} />
                </li>
              ))}
            </ul>
          </li>
          <button
            className={classNames(
              showHiddenLabels
                ? "bg-slate-200 text-slate-800"
                : "text-slate-600 hover:bg-slate-200 hover:text-slate-800",
              "group flex items-center  -mx-2 py-2 px-2 text-sm font-medium rounded-md"
            )}
            onClick={() => {
              setShowHiddenLabels(!showHiddenLabels);
            }}
          >
            <ChevronDownIcon
              className={classNames(
                showHiddenLabels
                  ? "rotate-180"
                  : " group-hover:text-gray-500",
                "h-5 w-5  text-gray-400  t"
              )}
              aria-hidden="true"
            />
            {isSidebarHovered && <p>{showHiddenLabels ? 'Less' : 'More'}</p>}
          </button>
          {showHiddenLabels && <>
            <li>
              <ul className="-mx-2 space-y-1">
                {advisor.filter(a => a.hidden).map((item) => (
                  <li key={item.name}>
                    <AdvisorNavLink item={item} isSidebarHovered={isSidebarHovered} />
                  </li>
                ))}
              </ul>
            </li>
            {<li>
              <div className="text-xs font-semibold leading-6 text-gray-400">Misc</div>
              <ul className="-mx-2 mt-2 space-y-1">
                {admin.map((item) => (
                  <li key={item.name}>
                    <AdvisorNavLink item={item} isSidebarHovered={isSidebarHovered} />
                  </li>
                ))}
              </ul>
            </li>}
            {<li>
              <div className="text-xs font-semibold leading-6 text-gray-400">Content</div>
              <ul className="-mx-2 mt-2 space-y-1">
                {content.map((item) => (
                  <li key={item.name}>
                    <AdvisorNavLink item={item} isSidebarHovered={isSidebarHovered} />
                  </li>
                ))}
              </ul>
            </li>}
            {isManager && <li>
              <div className="text-xs font-semibold leading-6 text-gray-400" aria-label="Manager Options">Manager</div>
              <ul className="-mx-2 mt-2 space-y-1">
                {manager.map((item) => (
                  <li key={item.name}>
                    <AdvisorNavLink item={item} isSidebarHovered={isSidebarHovered} />
                  </li>
                ))}
              </ul>
            </li>}
          </>}
        </ul>
      </nav>
    </div>
  );
}

function AdvisorTopBar({ setSidebarOpen, meetingMode, setMeetingMode }) {
  const nav = useNav();
  const { logout } = useAuth();
  const config = useConfig();
  const { householdId, isAdvisor } = config || {};

  // Fetch household name when advisor is viewing a specific household
  const householdsApi = new ApiRequest(API_ROUTES.HOUSEHOLDS, API_AUTH_TYPES.ADVISOR);
  const { data: householdData } = useQuery({
    queryFn: householdsApi.getFn({
      endpoint: "details_households",
      params: [householdId]
    }),
    queryKey: ["household-name", householdId],
    enabled: isAdvisor && householdId && householdId !== 'me',
  });

  const userNavigation = [
    {
      name: "Sign out",
      func: () => {
        try {
          nav("/");
          setTimeout(() => {
            logout();
          }, 1000);
        } catch (error) { }
      },
    },
    {
      name: "Client View",
      func: () => {
        try {
          nav("/dashboard");
        } catch (error) { }
      },
    },
  ];

  const [open, setOpen] = useState(false);
  const [showHHSearch, setShowHHSearch] = useState(false);

  return (
    <>

      <div className="top-0 z-40 flex h-12 bg-c-orange-abundo shadow" id="top-bar">
        {!meetingMode &&
          <button
            type="button"
            className="px-4 border-r border-gray-200 bg-white text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-c-orange-abundo lg:hidden"
            onClick={() => setSidebarOpen(true)}
            id="sidebar-toggle-button"
          >
            <span className="sr-only">Open sidebar</span>
            <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        }
        <div className="my-auto m-4  lg:ml-9 text-white flex items-center space-x-0.5">
          <img className="h-11 w-11 hidden sm:block" src="https://res.cloudinary.com/abundo-wealth-assets/image/upload/v1711124069/Abundo%20Wealth%20Assets/logos/bun_final_log-01_1_mqa5f7.svg" alt="" />
          {/* Display household name when advisor is viewing a specific household */}
          {isAdvisor && householdData?.name && (
            <div className="sm:pl-3 flex items-center">
              <span className="text-white text-sm font-medium max-w-[150px] sm:max-w-none truncate">
                {householdData.name}
              </span>
            </div>
          )}
        </div>
        <div className="flex-1  px-4 flex justify-end items-center" id="search-section">
          {/* HHSearch button - only show when viewing a specific household */}
          {isAdvisor && householdId && householdId !== 'me' && (
            <div className="mr-4 hidden sm:block">
              <HHSearch household_id={householdId} open={showHHSearch} setOpen={setShowHHSearch} />
            </div>
          )}
          <div className="flex items-center mr-4">
            <span className="text-sm font-medium text-white mr-2 hidden sm:block">Meeting Mode</span>
            <Switch
              checked={meetingMode}
              onChange={setMeetingMode}
              className={classNames(
                meetingMode ? 'bg-green-400' : 'bg-gray-200',
                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-c-orange-abundo'
              )}
            >
              <span className="sr-only">Use setting</span>
              <span
                className={classNames(
                  meetingMode ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                )}
              >
                <span
                  className={classNames(
                    meetingMode ? 'opacity-0 ease-in duration-200' : 'opacity-100 ease-out duration-100',
                    'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity'
                  )}
                  aria-hidden="true"
                >
                  <LockOpenIcon className="h-3 w-3 text-gray-400" />
                </span>
                <span
                  className={classNames(
                    meetingMode ? 'opacity-100 ease-out duration-100' : 'opacity-0 ease-in duration-200',
                    'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity'
                  )}
                  aria-hidden="true"
                >
                  <LockClosedIcon className="h-3 w-3 text-green-600" />
                </span>
              </span>
            </Switch>
          </div>
          {!meetingMode &&
            <>
              <ToolTipHoverContainer>
                <button
                  onClick={() => {
                    setOpen(true);
                  }}
                  id="search-button"
                >
                  {" "}
                  <SearchIcon className="h-6 w-6 mt-1 text-white" />
                  <ToolTipLeft>CTRL + Space</ToolTipLeft>
                </button>
              </ToolTipHoverContainer>
              <CommandPaletteHouseholdSearch open={open} setOpen={setOpen} />
            </>
          }
          <div className=" flex items-center lg:ml-6 space-x-1">
            <Notifications id="notifications-button" />
            <Menu as="div" className="ml-3 relative" id="profile-dropdown">
              <div className="">
                <Menu.Button className=" p-1 rounded-full hover:bg-white transition duration-300 ">
                  <span className="sr-only">Open user menu</span>
                  <UserIcon
                    className="h-6 w-6 text-white hover:text-gray-400 transition duration-300"
                    aria-hidden="true"
                  />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none" id="dropdown-menu">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <button
                          className={classNames(
                            active ? "bg-gray-100" : "",
                            "block px-4 py-2 text-sm text-gray-700 w-full text-left"
                          )}
                          onClick={() => {
                            item.func();
                          }}
                        >
                          {item.name}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </>
  );
}

function AdvisorNavLink({ item, isSidebarHovered }) {
  const nav = useNav()
  return (
    <button
      key={item.name}
      onClick={() => { nav(item.href) }}
      className={classNames(
        item.current
          ? "bg-slate-200 text-slate-900"
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-200",
        "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md"
      )}
    >
      <item.icon.outline
        className={classNames(
          item.current
            ? "text-slate-700"
            : "text-slate-500 group-hover:text-slate-700",
          "flex-shrink-0 h-6 w-6",
          isSidebarHovered && "mr-4"
        )}
        aria-hidden="true"
      />
      {isSidebarHovered && <span>{item.name}</span>}
    </button>
  )
}

function ClientDesktopSidebar({ clientNavProps }) {
  const { nav, filteredNavigation, externalLinks, resources, bottomNav, topLevelLegal, legal, isAdvisor, advisor: bunLink, budgetDropdownHover, handleBudgetHoverEnter, handleBudgetHoverLeave } = clientNavProps;
  return (
    <div className="flex flex-col flex-grow border-r pt-5 bg-gray-50 overflow-y-auto">
      <a
        className="flex items-center justify-center flex-shrink-0 px-4"
        href="/dashboard"
      >
        <img className="h-14 w-auto" src={abundoLogo} alt="abundo" />
      </a>
      <div className="mt-5 flex-grow flex flex-col">
        <div>
          {" "}
          <nav className="flex-1 pb-4 space-y-1  mx-2">
            {" "}
            {filteredNavigation && filteredNavigation.map((item) =>
              item.children ? (
                <div
                  key={item.name}
                  onMouseEnter={() => item.name === "Budget" && handleBudgetHoverEnter && handleBudgetHoverEnter()}
                  onMouseLeave={() => item.name === "Budget" && handleBudgetHoverLeave && handleBudgetHoverLeave()}
                >
                  <Disclosure as="div" defaultOpen={item.current || (item.name === "Budget" && budgetDropdownHover)}>
                    {({ open }) => (
                      <>
                        <Disclosure.Button
                          className={classNames(
                            item.current
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                            'group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md'
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500',
                              'mr-3 h-6 w-6 flex-shrink-0'
                            )}
                            aria-hidden="true"
                          />
                          <span className="flex-1 text-left">{item.name}</span>
                          <ChevronDownIcon
                            className={`${open || (item.name === "Budget" && budgetDropdownHover) ? 'transform rotate-180' : ''} w-5 h-5 text-gray-400 transition-transform duration-200`}
                          />
                        </Disclosure.Button>
                        <Transition
                          show={open || (item.name === "Budget" && budgetDropdownHover)}
                          enter="transition duration-200 ease-out"
                          enterFrom="transform scale-95 opacity-0"
                          enterTo="transform scale-100 opacity-100"
                          leave="transition duration-150 ease-in"
                          leaveFrom="transform scale-100 opacity-100"
                          leaveTo="transform scale-95 opacity-0"
                        >
                          <Disclosure.Panel className="space-y-1 pl-5" static={item.name === "Budget" && budgetDropdownHover}>
                            {item.children.map((subItem) => (
                              <button
                                key={subItem.name}
                                onClick={() => nav(subItem.href)}
                                className={classNames(
                                  subItem.current
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                                  'group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md'
                                )}
                              >
                                {subItem.icon && (
                                  <subItem.icon
                                    className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                    aria-hidden="true"
                                  />
                                )}
                                {subItem.name}
                              </button>
                            ))}
                          </Disclosure.Panel>
                        </Transition>
                      </>
                    )}
                  </Disclosure>
                </div>
              ) : (
                <button
                  key={item.name}
                  onClick={() => nav(item.href)}
                  className={classNames(
                    item.current
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                    "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md"
                  )}


                >

                  <item.icon
                    className={classNames(
                      item.current
                        ? "text-gray-500"
                        : "text-gray-400 group-hover:text-gray-500",
                      "mr-3 flex-shrink-0 h-6 w-6"
                    )}
                    aria-hidden="true"
                  />
                  {item.name}
                </button>
              )
            )}


            <Disclosure defaultOpen={false}>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex justify-between w-full px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md">
                    <span className="flex items-center">
                      <LibraryIcon
                        className="mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Resources
                    </span>
                    <ChevronDownIcon
                      className={`${open ? 'transform rotate-180' : ''
                        } w-5 h-5 text-gray-400`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-2 pt-2 pb-2 space-y-1">
                    {resources.map((item) => (
                      item.target ? (
                        <a
                          key={item.name}
                          href={item.href}
                          rel={item.rel}
                          target={item.target}
                          className={classNames(
                            item.current
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-gray-500"
                                : "text-gray-400 group-hover:text-gray-500",
                              "mr-3 flex-shrink-0 h-6 w-6"
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </a>
                      ) : (
                        <button
                          key={item.name}
                          onClick={() => nav(item.href)}
                          className={classNames(
                            item.current
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                            "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-gray-500"
                                : "text-gray-400 group-hover:text-gray-500",
                              "mr-3 flex-shrink-0 h-6 w-6"
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </button>
                      )
                    ))}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>

            <Disclosure defaultOpen={false}>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex justify-between w-full px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md">
                    <span className="flex items-center">
                      <span className="ml-9">More</span>
                    </span>
                    <ChevronDownIcon
                      className={`${open ? 'transform rotate-180' : ''
                        } w-5 h-5 text-gray-400`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-2 pt-2 pb-2 space-y-1">
                    {legal.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        rel={item.rel}
                        target={item.target}
                        className={classNames(
                          item.current
                            ? "bg-gray-100 text-gray-900"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                          "group flex items-center px-2 py-2 text-sm font-medium rounded-md"
                        )}
                      >
                        {item.icon && (
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-gray-500"
                                : "text-gray-400 group-hover:text-gray-500",
                              "mr-3 flex-shrink-0 h-6 w-6"
                            )}
                            aria-hidden="true"
                          />
                        )}
                        {item.name}
                      </a>
                    ))}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </nav>{" "}
        </div>

      </div>
    </div>
  )
}